import { Badge } from '@thefarmersfront/kitchen';

import type { ColDef, ICellRendererParams, IRowNode, ValueFormatterParams } from 'ag-grid-community';

import { TextButtonCell, TooltipTextCell } from '~/components/common';

import { formatDate } from '~/utils/date';

import { COLLECTING_SHIPPING_LABEL_STATUS_BADGE_COLOR, DATE_FORMAT } from '~/constants';

import type { CollectingResultReportItem } from '~/types/delivery-agency.types';

const getDeliveryAgencyCollectingResultReportsColumnDefs = ({
  currentPage,
  pageSize,
  onClickViewDetail,
}: {
  currentPage: number;
  pageSize: number;
  onClickViewDetail?: (report: CollectingResultReportItem) => void;
}) => {
  const columnDefs: ColDef<CollectingResultReportItem>[] = [
    {
      colId: 'rowNumber',
      headerName: 'No',
      valueGetter: ({ node }) => (node?.rowIndex ?? 0) + 1 + currentPage * pageSize,
      width: 72,
    },
    {
      field: 'collectingRequestCode',
      headerName: '반품요청번호',
      width: 160,
    },
    {
      field: 'shippingLabelNumber',
      headerName: '회수 운송장번호',
      width: 232,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<CollectingResultReportItem, CollectingResultReportItem['shippingLabelNumber']>) =>
        value || '-',
    },
    // 화면설계: 운송장번호는 텍스트로 두고 상세 진입은 별도 버튼 컬럼으로 분리 (배송 결과 리포트와 동일)
    ...(onClickViewDetail
      ? [
          {
            colId: 'viewDetail',
            headerName: '',
            minWidth: 80,
            width: 80,
            cellRenderer: TextButtonCell,
            cellRendererParams: {
              children: '상세보기',
              showDashIfNoValue: false,
              onClick: ({ data }: IRowNode<CollectingResultReportItem>) => {
                if (!data || !onClickViewDetail) return;
                onClickViewDetail(data);
              },
            },
            cellStyle: { display: 'flex', alignItems: 'center' },
          } satisfies ColDef<CollectingResultReportItem>,
        ]
      : []),
    {
      field: 'originalOutboundCode',
      headerName: '출고번호',
      width: 160,
    },
    {
      field: 'originalOrderCodes',
      headerName: '판매처 주문번호(원주문)',
      width: 190,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<CollectingResultReportItem, CollectingResultReportItem['originalOrderCodes']>) =>
        value?.length ? value.join(', ') : '-',
      cellRenderer: TooltipTextCell,
    },
    {
      field: 'collectingRequestedDate',
      headerName: '반품접수일',
      width: 110,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<CollectingResultReportItem, CollectingResultReportItem['collectingRequestedDate']>) =>
        formatDate(value, DATE_FORMAT.DATE) ?? '-',
    },
    {
      field: 'collectingShippingLabelStatus',
      headerName: '회수 상태',
      cellDataType: false,
      width: 120,
      cellRenderer: Badge,
      cellRendererParams: ({
        value,
      }: ICellRendererParams<
        CollectingResultReportItem,
        CollectingResultReportItem['collectingShippingLabelStatus']
      >) => ({
        variant: 'light',
        status: true,
        color: value ? COLLECTING_SHIPPING_LABEL_STATUS_BADGE_COLOR[value.code] : undefined,
        children: value?.description || '-',
      }),
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      colId: 'deliveryLocation',
      headerName: '회수 요청 장소',
      width: 140,
      valueGetter: ({ data }) => data?.collectingSite.deliveryLocation?.description ?? '-',
    },
    {
      colId: 'deliveryLocationDetail',
      headerName: '회수 요청 장소 상세',
      width: 170,
      valueGetter: ({ data }) => data?.collectingSite.deliveryLocationDetail || '-',
      cellRenderer: TooltipTextCell,
    },
    {
      colId: 'collectingFailureReasonCategory',
      headerName: '회수 실패 사유',
      width: 170,
      valueGetter: ({ data }) => data?.collectingFailureReasonCategory?.description ?? '-',
    },
    {
      colId: 'contactName',
      headerName: '원주문 수령자명',
      width: 120,
      valueGetter: ({ data }) => data?.contact.name ?? '-',
    },
    {
      colId: 'contactTel',
      headerName: '고객 연락처',
      width: 140,
      valueGetter: ({ data }) => data?.contact.tel ?? '-',
    },
    {
      colId: 'refineAddress',
      headerName: '정제 주소',
      flex: 1,
      minWidth: 220,
      valueGetter: ({ data }) => data?.collectingSite.refineAddress ?? '-',
      cellRenderer: TooltipTextCell,
    },
  ];

  return { columnDefs };
};

export default getDeliveryAgencyCollectingResultReportsColumnDefs;
